/**
 * Generador de bytes ESC/POS, sin dependencias.
 *
 * No usamos `esc-pos-encoder` ni parientes porque arrastran polyfills de Node
 * (`Buffer`) que webpack 5 ya no inyecta: si eso rompe, rompe `react-scripts build`
 * para todo el equipo. El ticket que necesitamos son seis comandos.
 *
 * Todo lo de aquí está verificado contra la impresora del club el 27-ago-2026
 * (Jolimark TP510, módulo ISSC BT5050):
 *   - `ESC t 2` (CP850) saca los acentos legibles
 *   - el ancho útil son 32 columnas exactas
 *   - NO tiene cuchilla, y la barra de corte queda al ras del cabezal
 */

/** Columnas que caben de margen a margen en papel de 58 mm. */
export const ANCHO = 32;

/**
 * Avances finales. Sin cuchilla se rasga a mano, y la barra de corte está tan
 * pegada al cabezal que con menos de esto te llevas por delante la última línea.
 */
export const AVANCE_FINAL = 7;

/**
 * CP850. El euro NO está aquí a propósito: en CP850 el 0xD5 es otra cosa (solo
 * CP858 lo trae), así que los importes se escriben "EUR" en vez de arriesgar un
 * símbolo que sale distinto en cada impresora.
 */
const CP850 = {
  "á": 0xa0, "é": 0x82, "í": 0xa1, "ó": 0xa2, "ú": 0xa3,
  "Á": 0xb5, "É": 0x90, "Í": 0xd6, "Ó": 0xe0, "Ú": 0xe9,
  "ñ": 0xa4, "Ñ": 0xa5, "ü": 0x81, "Ü": 0x9a, "ç": 0x87, "Ç": 0x80,
  "à": 0x85, "è": 0x8a, "ì": 0x8d, "ò": 0x95, "ù": 0x97,
  "¿": 0xa8, "¡": 0xad, "º": 0xa7, "ª": 0xa6, "·": 0xfa,
};

/** Texto a bytes CP850. Lo que no sabemos representar sale como "?" y no rompe nada. */
export function aBytes(texto) {
  const out = [];
  for (const caracter of String(texto)) {
    const codigo = caracter.codePointAt(0);
    if (codigo < 0x80) out.push(codigo);
    else if (CP850[caracter] !== undefined) out.push(CP850[caracter]);
    else out.push(0x3f);
  }
  return out;
}

/**
 * Parte un texto en líneas de `ancho`, con sangría en las continuaciones.
 *
 * Existe porque 32 columnas es MUY poco: "Hamburguesa completa con extra de queso"
 * son 39 caracteres. Sin esto la impresora lo arrastra sola y el corte cae donde
 * quiera, en mitad de una palabra.
 */
export function envolver(texto, ancho = ANCHO, sangria = "") {
  const palabras = String(texto).trim().split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return [""];

  const lineas = [];
  let actual = "";
  let prefijo = "";

  const cerrar = () => {
    if (actual) lineas.push(prefijo + actual);
    actual = "";
    prefijo = sangria;
  };

  for (let palabra of palabras) {
    // Una palabra sola más larga que el renglón (una URL, una referencia) hay que
    // trocearla a lo bruto: no hay hueco donde partirla bien.
    while (palabra.length > ancho - prefijo.length) {
      cerrar();
      const hueco = ancho - prefijo.length;
      lineas.push(prefijo + palabra.slice(0, hueco));
      palabra = palabra.slice(hueco);
      prefijo = sangria;
    }
    if (!actual) actual = palabra;
    else if (prefijo.length + actual.length + 1 + palabra.length <= ancho) actual += " " + palabra;
    else { cerrar(); actual = palabra; }
  }
  cerrar();
  return lineas;
}

/**
 * Una línea con algo pegado a la izquierda y algo pegado a la derecha.
 * Si no caben los dos, manda el de la derecha: es el importe.
 */
export function dosColumnas(izquierda, derecha, ancho = ANCHO) {
  const der = String(derecha);
  const hueco = ancho - der.length - 1;
  if (hueco <= 0) return der.slice(-ancho);
  const izq = String(izquierda).slice(0, hueco);
  return izq + " ".repeat(ancho - izq.length - der.length) + der;
}

/** Constructor encadenable. `bytes()` cierra y devuelve lo que se manda por BLE. */
export function crearTicket() {
  const salida = [];
  const meter = (arr) => { for (const b of arr) salida.push(b); };

  const api = {
    /** ESC @ (reinicia estado) + ESC t 2 (CP850). Siempre lo primero. */
    inicializar() { meter([0x1b, 0x40, 0x1b, 0x74, 0x02]); return api; },
    alineacion(donde) {
      const n = donde === "centro" ? 1 : donde === "derecha" ? 2 : 0;
      meter([0x1b, 0x61, n]);
      return api;
    },
    negrita(activa) { meter([0x1b, 0x45, activa ? 1 : 0]); return api; },
    texto(txt) { meter(aBytes(txt)); return api; },
    linea(txt = "") { meter(aBytes(txt)); meter([0x0a]); return api; },
    lineas(arr) { for (const l of arr) api.linea(l); return api; },
    separador(ancho = ANCHO) { return api.linea("-".repeat(ancho)); },
    avance(n = 1) { for (let i = 0; i < n; i += 1) meter([0x0a]); return api; },
    bytes() { return new Uint8Array(salida); },
  };
  return api;
}
